import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChannelType,
  MessageType,
  Permissions as P,
  type Channel,
  type Message,
  type ThreadList,
} from '@comms/shared';
import { addMessages, displayName, isUnread, mentionsMe, setMyPollVote, useStore } from '../store';
import { openModal, useUi } from '../store/ui';
import { api, uploadFile } from '../lib/api';
import { has, usePermissions } from '../hooks/permissions';
import { MessageList, type MessageActions, type ViewMessage } from '../components/MessageList';
import { Composer, type MentionCandidate } from '../components/Composer';
import { InteractionNotices } from '../components/CommandInput';
import { loadCommands, runCommand, sendComponent, useCommands } from '../store/commands';
import { MemberList } from '../components/MemberList';
import { ChannelNotifyButton } from '../components/NotificationMenu';
import { Markdown } from '../components/Markdown';
import { Icon, Spinner, cx } from '../components/ui';
import { TypingIndicator } from '../components/TypingIndicator';
import { MobileSheet, NavButton } from '../components/MobileNav';
import { useIsMobile, useSwipe } from '../hooks/mobile';

const EMPTY: Message[] = [];

export function toView(m: Message, guildId: string | null, highlight = false): ViewMessage {
  const who = () => displayName(useStore.getState(), m.author.id, guildId);
  const system =
    m.type === MessageType.MEMBER_JOIN
      ? `${who()} joined the server. Say hi!`
      : m.type === MessageType.THREAD_CREATED
        ? `${who()} started a thread: ${m.content}`
        : m.type === MessageType.DM_MODE_CHANGE
          ? m.content === 'e2ee'
            ? `${who()} turned on end-to-end encryption. New messages are encrypted.`
            : `${who()} switched this conversation to standard mode. New messages are stored on the server.`
          : undefined;
  return {
    id: m.id,
    authorId: m.author.id,
    content: m.content,
    createdAt: m.createdAt,
    editedAt: m.editedAt,
    attachments: m.attachments,
    embeds: m.embeds,
    reactions: m.reactions,
    pinned: m.pinned,
    replyTo: m.referencedMessage
      ? {
          id: m.referencedMessage.id,
          authorId: m.referencedMessage.author.id,
          content: m.referencedMessage.content,
        }
      : null,
    system,
    highlight,
    thread: m.thread ?? null,
    automod: m.type === MessageType.AUTOMOD_ACTION,
    bot: m.author.bot,
    authorName: m.webhookId ? m.author.displayName : null,
    interaction: m.interaction
      ? { name: m.interaction.name, userId: m.interaction.user.id, type: m.interaction.type }
      : null,
    components: m.components,
    stickers: m.stickers,
    poll: m.poll ?? null,
  };
}

type Panel = 'members' | 'pins' | 'search' | 'threads';

export function ChannelView({ guildId, channelId }: { guildId: string; channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const bucket = useStore((s) => s.messages[channelId]);
  const messages = bucket?.items ?? EMPTY;
  const perms = usePermissions(guildId, channelId);
  const commands = useCommands((s) => s.byGuild[guildId]);
  const noticeCount = useCommands((s) => s.notices[channelId]?.length ?? 0);
  const showMembers = useUi((s) => s.showMembers);
  const mobile = useIsMobile();
  // Phones start on the conversation; the member list is a drawer (swipe left or the button).
  const [panel, setPanel] = useState<Panel | null>(() =>
    showMembers && !window.matchMedia('(max-width: 767px)').matches ? 'members' : null,
  );
  const swipeRoot = useSwipe({ onLeft: () => setPanel('members') }, mobile && panel === null);
  const [replyTo, setReplyTo] = useState<ViewMessage | null>(null);
  const [query, setQuery] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const thread = channel?.threadMetadata ? channel : null;
  const parent = useStore((s) => (channel?.parentId ? s.channels[channel.parentId] : undefined));
  const parentPerms = usePermissions(guildId, thread ? thread.parentId : null);
  const joined = useStore((s) => !!s.joinedThreads[channelId]);
  const meId = useStore((s) => s.me?.id);
  const stickers = useStore((s) => s.guilds[guildId]?.stickers);

  useEffect(() => {
    if (!bucket?.loaded && has(perms, P.READ_MESSAGE_HISTORY)) {
      api<Message[]>('GET', `/channels/${channelId}/messages?limit=50`)
        .then((ms) => addMessages(channelId, ms.reverse(), ms.length === 50))
        .catch(() => addMessages(channelId, [], false));
    }
  }, [channelId, bucket?.loaded, perms]);

  // Mark read when the latest message is visible.
  const lastId = messages.at(-1)?.id;
  const unread = useStore(
    (s) => isUnread(s, channelId) || (s.readStates[channelId]?.mentionCount ?? 0) > 0,
  );
  useEffect(() => {
    if (!lastId || !unread) return;
    const ack = () =>
      !document.hidden &&
      void api('POST', `/channels/${channelId}/messages/${lastId}/ack`).catch(() => {});
    const t = window.setTimeout(ack, 500);
    document.addEventListener('visibilitychange', ack);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('visibilitychange', ack);
    };
  }, [channelId, lastId, unread]);

  const loadOlder = useCallback(async () => {
    const first = useStore.getState().messages[channelId]?.items[0];
    if (!first) return;
    const ms = await api<Message[]>(
      'GET',
      `/channels/${channelId}/messages?limit=50&before=${first.id}`,
    );
    addMessages(channelId, ms.reverse(), ms.length === 50);
  }, [channelId]);

  const views = useMemo(
    () => messages.map((m) => toView(m, guildId, mentionsMe(m))),
    [messages, guildId],
  );

  const actions: MessageActions = {
    onReply: setReplyTo,
    onEdit: async (m, content) => {
      await api('PATCH', `/channels/${channelId}/messages/${m.id}`, { content });
    },
    onDelete: async (m) => {
      await api('DELETE', `/channels/${channelId}/messages/${m.id}`);
    },
    onReact: (m, emoji, remove) =>
      void api(
        remove ? 'DELETE' : 'PUT',
        `/channels/${channelId}/messages/${m.id}/reactions/${encodeURIComponent(emoji)}/@me`,
      ),
    onPin: (m, pin) => void api(pin ? 'PUT' : 'DELETE', `/channels/${channelId}/pins/${m.id}`),
    canDeleteAny: has(perms, P.MANAGE_MESSAGES),
    canPin: has(perms, P.PIN_MESSAGES) || has(perms, P.MANAGE_MESSAGES),
    canReact: has(perms, P.ADD_REACTIONS),
    onVote: async (m, answerIds) => {
      await api('PUT', `/channels/${channelId}/polls/${m.id}/votes/@me`, { answerIds });
      setMyPollVote(channelId, m.id, answerIds);
    },
    onComponent: (m, customId, label, values) =>
      void sendComponent(channelId, m, customId, label, values).catch((err: unknown) =>
        alert(err instanceof Error ? err.message : 'Something went wrong'),
      ),
    onClosePoll: (m) =>
      confirm('End this poll now?') &&
      void api('POST', `/channels/${channelId}/polls/${m.id}/close`),
    onCreateThread:
      channel?.type === ChannelType.GUILD_TEXT && has(perms, P.CREATE_PUBLIC_THREADS)
        ? (m) =>
            openModal({
              kind: 'createThread',
              guildId,
              channelId,
              messageId: m.id,
              name: m.content.slice(0, 60) || 'New thread',
            })
        : undefined,
  };

  const mentionCandidates = useCallback(
    (q: string): MentionCandidate[] => {
      const s = useStore.getState();
      const members = Object.values(s.members[guildId] ?? {})
        .filter(
          (m) =>
            m.user.username.includes(q) ||
            (m.nickname ?? m.user.displayName).toLowerCase().includes(q),
        )
        .map((m) => ({
          id: m.user.id,
          label: m.nickname ?? m.user.displayName,
          sub: m.user.username,
          insert: `<@${m.user.id}>`,
        }));
      const roles = (s.guilds[guildId]?.roles ?? [])
        .filter(
          (r) =>
            r.id !== guildId &&
            (r.mentionable || has(perms, P.MENTION_EVERYONE)) &&
            r.name.toLowerCase().includes(q),
        )
        .map((r) => ({ id: r.id, label: `@${r.name}`, sub: 'role', insert: `<@&${r.id}>` }));
      const everyone =
        has(perms, P.MENTION_EVERYONE) && 'everyone'.startsWith(q)
          ? [{ id: 'everyone', label: '@everyone', insert: '@everyone' }]
          : [];
      return [...everyone, ...members, ...roles];
    },
    [guildId, perms],
  );

  async function send(content: string, files: File[]) {
    const attachmentIds: string[] = [];
    for (const f of files) attachmentIds.push((await uploadFile(f, f.name)).id);
    await api('POST', `/channels/${channelId}/messages`, {
      content,
      attachmentIds,
      ...(replyTo ? { referenceId: replyTo.id } : {}),
    });
    setReplyTo(null);
  }

  if (!channel)
    return (
      <div className="flex flex-1 items-center justify-center text-text-3">Channel not found.</div>
    );
  const canSend = has(perms, P.SEND_MESSAGES);
  const label = thread ? channel.name : `#${channel.name}`;

  return (
    <div ref={swipeRoot} className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-bg-0 px-4 shadow-sm">
          <NavButton />
          {thread && parent ? (
            <>
              <Link
                to={`/channels/${guildId}/${parent.id}`}
                className="flex items-center gap-1 text-text-3 hover:text-text-1"
              >
                <Icon
                  name={parent.type === ChannelType.GUILD_FORUM ? 'forum' : 'hash'}
                  className="h-5 w-5"
                />
                {parent.name}
              </Link>
              <span className="text-text-3">›</span>
              <Icon name="thread" className="h-5 w-5 text-text-3" />
            </>
          ) : (
            <Icon name="hash" className="h-6 w-6 text-text-3" />
          )}
          <h1 className="truncate font-semibold text-text-0">{channel.name}</h1>
          {thread && (
            <ThreadControls
              thread={thread}
              joined={joined}
              canManage={has(parentPerms, P.MANAGE_THREADS)}
              isOwner={thread.ownerId === meId}
              canSend={canSend}
              guildId={guildId}
            />
          )}
          {channel.topic && (
            <span className="ml-2 truncate border-l border-bg-3 pl-3 text-sm text-text-3">
              {channel.topic}
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            {channel.type === ChannelType.GUILD_TEXT && (
              <button
                type="button"
                title="Threads"
                onClick={() => setPanel(panel === 'threads' ? null : 'threads')}
                className={cx(
                  'hover:text-text-0',
                  panel === 'threads' ? 'text-text-0' : 'text-text-2',
                )}
              >
                <Icon name="thread" />
              </button>
            )}
            <ChannelNotifyButton channel={channel} />
            <button
              type="button"
              title="Pinned messages"
              onClick={() => setPanel(panel === 'pins' ? null : 'pins')}
              className={cx('hover:text-text-0', panel === 'pins' ? 'text-text-0' : 'text-text-2')}
            >
              <Icon name="pin" />
            </button>
            <button
              type="button"
              title="Member list"
              onClick={() => {
                const next = panel === 'members' ? null : 'members';
                useUi.setState({ showMembers: next === 'members' });
                setPanel(next);
              }}
              className={cx(
                'hover:text-text-0',
                panel === 'members' ? 'text-text-0' : 'text-text-2',
              )}
            >
              <Icon name="users" />
            </button>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (query.trim()) {
                  setSearchQ(query.trim());
                  setPanel('search');
                }
              }}
            >
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="w-24 rounded bg-bg-0 px-2 py-1 text-sm outline-none transition-all focus:w-36 md:w-36 md:focus:w-56"
              />
            </form>
          </div>
        </header>
        <MessageList
          messages={views}
          guildId={guildId}
          actions={actions}
          hasMore={bucket?.hasMore ?? true}
          loadOlder={loadOlder}
          footer={<InteractionNotices channelId={channelId} guildId={guildId} />}
          footerCount={noticeCount}
          intro={
            <div>
              <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-bg-3">
                <Icon name={thread ? 'thread' : 'hash'} className="h-10 w-10 text-text-0" />
              </div>
              <h2 className="text-3xl font-bold text-text-0">
                {thread ? channel.name : `Welcome to #${channel.name}!`}
              </h2>
              <p className="text-text-3">
                {thread
                  ? `This is the start of the thread in #${parent?.name ?? ''}.`
                  : `This is the start of the #${channel.name} channel.`}
              </p>
            </div>
          }
        />
        <TypingIndicator channelId={channelId} guildId={guildId} />
        {thread?.threadMetadata?.archived && canSend && (
          <div className="mx-4 mb-1 rounded bg-bg-1 px-3 py-1.5 text-sm text-text-3">
            This thread is archived. Sending a message will reopen it.
          </div>
        )}
        <Composer
          placeholder={`Message ${label}`}
          disabled={!canSend}
          disabledReason={
            thread?.threadMetadata?.locked
              ? 'This thread is locked. Only moderators can reply.'
              : 'You do not have permission to send messages in this channel.'
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSend={send}
          onTyping={() => void api('POST', `/channels/${channelId}/typing`).catch(() => {})}
          mentionCandidates={mentionCandidates}
          canAttach={has(perms, P.ATTACH_FILES)}
          guildId={guildId}
          stickers={stickers}
          onSendGif={
            has(perms, P.EMBED_LINKS)
              ? async (gif) => {
                  await api('POST', `/channels/${channelId}/messages`, {
                    gifId: gif.id,
                    ...(replyTo ? { referenceId: replyTo.id } : {}),
                  });
                  setReplyTo(null);
                }
              : undefined
          }
          onCreatePoll={
            has(perms, P.SEND_POLLS)
              ? () => openModal({ kind: 'createPoll', channelId })
              : undefined
          }
          onSendSticker={async (id) => {
            await api('POST', `/channels/${channelId}/messages`, { stickerIds: [id] });
          }}
          commands={has(perms, P.USE_APPLICATION_COMMANDS) ? commands : undefined}
          onCommandsWanted={() => loadCommands(guildId)}
          onRunCommand={
            has(perms, P.USE_APPLICATION_COMMANDS)
              ? (cmd, options) => runCommand(channelId, cmd, options)
              : undefined
          }
        />
      </div>
      {panel === 'members' && (
        <MobileSheet onClose={() => setPanel(null)}>
          <MemberList guildId={guildId} channelId={channelId} />
        </MobileSheet>
      )}
      {panel === 'pins' && (
        <MobileSheet full onClose={() => setPanel(null)}>
          <PinsPanel channelId={channelId} guildId={guildId} onClose={() => setPanel(null)} />
        </MobileSheet>
      )}
      {panel === 'threads' && (
        <MobileSheet full onClose={() => setPanel(null)}>
          <ThreadsPanel
            channelId={channelId}
            guildId={guildId}
            canCreate={has(perms, P.CREATE_PUBLIC_THREADS) || has(perms, P.CREATE_PRIVATE_THREADS)}
            onClose={() => setPanel(null)}
          />
        </MobileSheet>
      )}
      {panel === 'search' && (
        <MobileSheet full onClose={() => setPanel(null)}>
          <SearchPanel guildId={guildId} query={searchQ} onClose={() => setPanel(null)} />
        </MobileSheet>
      )}
    </div>
  );
}

function SidePanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-bg-0 bg-bg-1">
      <div className="flex h-12 items-center justify-between border-b border-bg-0 px-4 font-semibold text-text-0">
        {title}
        <button type="button" onClick={onClose} className="text-text-3 hover:text-text-0">
          <Icon name="x" />
        </button>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">{children}</div>
    </aside>
  );
}

function MiniMessage({ m, guildId }: { m: Message; guildId: string }) {
  const name = useStore((s) => displayName(s, m.author.id, guildId));
  return (
    <div className="rounded bg-bg-2 p-3">
      <div className="mb-1 flex items-baseline gap-2">
        <span className="font-medium text-text-0">{name}</span>
        <span className="text-xs text-text-3">{new Date(m.createdAt).toLocaleString()}</span>
      </div>
      <div className="text-sm text-text-1">
        <Markdown
          content={m.content || (m.attachments.length ? '[attachment]' : '')}
          guildId={guildId}
        />
      </div>
    </div>
  );
}

function PinsPanel({
  channelId,
  guildId,
  onClose,
}: {
  channelId: string;
  guildId: string;
  onClose: () => void;
}) {
  const [pins, setPins] = useState<Message[] | null>(null);
  const version = useStore((s) => s.messages[channelId]?.items.filter((m) => m.pinned).length);
  useEffect(() => {
    api<Message[]>('GET', `/channels/${channelId}/pins`).then(setPins, () => setPins([]));
  }, [channelId, version]);
  return (
    <SidePanel title="Pinned Messages" onClose={onClose}>
      {!pins && <Spinner />}
      {pins?.length === 0 && <p className="text-sm text-text-3">No pinned messages yet.</p>}
      {pins?.map((m) => (
        <MiniMessage key={m.id} m={m} guildId={guildId} />
      ))}
    </SidePanel>
  );
}

function SearchPanel({
  guildId,
  query,
  onClose,
}: {
  guildId: string;
  query: string;
  onClose: () => void;
}) {
  const [res, setRes] = useState<{ total: number; messages: Message[] } | null>(null);
  useEffect(() => {
    setRes(null);
    api<{ total: number; messages: Message[] }>(
      'GET',
      `/guilds/${guildId}/messages/search?q=${encodeURIComponent(query)}`,
    ).then(setRes, () => setRes({ total: 0, messages: [] }));
  }, [guildId, query]);
  return (
    <SidePanel title={res ? `${res.total} results` : 'Searching…'} onClose={onClose}>
      {!res && <Spinner />}
      {res?.messages.map((m) => (
        <div key={m.id}>
          <div className="mb-1 text-xs font-semibold text-text-3">
            #{useStore.getState().channels[m.channelId]?.name}
          </div>
          <MiniMessage m={m} guildId={guildId} />
        </div>
      ))}
    </SidePanel>
  );
}

function ThreadControls({
  thread,
  joined,
  canManage,
  isOwner,
  canSend,
  guildId,
}: {
  thread: Channel;
  joined: boolean;
  canManage: boolean;
  isOwner: boolean;
  canSend: boolean;
  guildId: string;
}) {
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const meta = thread.threadMetadata!;
  const ownerOk = canManage || (isOwner && !meta.locked);
  const patch = (body: Record<string, unknown>) =>
    void api('PATCH', `/channels/${thread.id}`, body).catch((e: unknown) =>
      alert(e instanceof Error ? e.message : 'Failed'),
    );
  const items = [
    ownerOk && {
      label: 'Rename',
      action: () => {
        const name = prompt('Thread name', thread.name ?? '');
        if (name?.trim()) patch({ name: name.trim() });
      },
    },
    meta.archived
      ? (canManage || (!meta.locked && canSend)) && {
          label: 'Unarchive',
          action: () => patch({ archived: false }),
        }
      : ownerOk && { label: 'Archive', action: () => patch({ archived: true }) },
    canManage && {
      label: meta.locked ? 'Unlock' : 'Lock',
      action: () => patch({ locked: !meta.locked }),
    },
    canManage && {
      label: 'Delete thread',
      danger: true,
      action: async () => {
        if (!confirm(`Delete thread "${thread.name}"?`)) return;
        await api('DELETE', `/channels/${thread.id}`);
        navigate(`/channels/${guildId}/${thread.parentId}`);
      },
    },
  ].filter(Boolean) as { label: string; action: () => void; danger?: boolean }[];
  return (
    <div className="relative flex items-center gap-2">
      {meta.locked && <Icon name="lock" className="h-4 w-4 text-text-3" />}
      {meta.archived && <span className="text-xs text-text-3">archived</span>}
      <button
        type="button"
        onClick={() =>
          void api(joined ? 'DELETE' : 'PUT', `/channels/${thread.id}/thread-members/@me`)
        }
        className="rounded bg-bg-3 px-2 py-0.5 text-xs font-medium text-text-1 hover:bg-bg-active"
      >
        {joined ? 'Leave' : 'Join'}
      </button>
      {items.length > 0 && (
        <button
          type="button"
          title="Thread settings"
          onClick={() => setMenu(!menu)}
          className="text-text-3 hover:text-text-0"
        >
          <Icon name="settings" className="h-4 w-4" />
        </button>
      )}
      {menu && (
        <div className="absolute left-0 top-7 z-20 w-44 rounded bg-bg-0 p-1.5 shadow-xl">
          {items.map((i) => (
            <button
              key={i.label}
              type="button"
              onClick={() => {
                setMenu(false);
                i.action();
              }}
              className={cx(
                'block w-full rounded px-2 py-1.5 text-left text-sm',
                i.danger
                  ? 'text-danger hover:bg-danger hover:text-white'
                  : 'text-text-2 hover:bg-accent hover:text-white',
              )}
            >
              {i.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ThreadsPanel({
  channelId,
  guildId,
  canCreate,
  onClose,
}: {
  channelId: string;
  guildId: string;
  canCreate: boolean;
  onClose: () => void;
}) {
  const [archived, setArchived] = useState(false);
  const [list, setList] = useState<ThreadList | null>(null);
  // Refresh when threads of this channel change.
  const version = useStore((s) =>
    Object.values(s.channels)
      .filter((c) => c.parentId === channelId && c.threadMetadata)
      .map((c) => `${c.id}:${c.threadMetadata!.archived}:${c.threadMetadata!.messageCount}`)
      .join(','),
  );
  useEffect(() => {
    api<ThreadList>('GET', `/channels/${channelId}/threads?archived=${archived}`).then(
      setList,
      () => setList({ threads: [], hasMore: false }),
    );
  }, [channelId, archived, version]);
  return (
    <SidePanel title="Threads" onClose={onClose}>
      <div className="flex items-center gap-2">
        {(['Active', 'Archived'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setArchived(t === 'Archived')}
            className={cx(
              'rounded px-2 py-1 text-sm',
              archived === (t === 'Archived') ? 'bg-bg-active text-text-0' : 'text-text-3',
            )}
          >
            {t}
          </button>
        ))}
        {canCreate && (
          <button
            type="button"
            onClick={() => openModal({ kind: 'createThread', guildId, channelId })}
            className="ml-auto rounded bg-accent px-2 py-1 text-sm text-white hover:bg-accent-hover"
          >
            New thread
          </button>
        )}
      </div>
      {!list && <Spinner />}
      {list?.threads.length === 0 && (
        <p className="text-sm text-text-3">No {archived ? 'archived' : 'active'} threads.</p>
      )}
      {list?.threads.map((t) => (
        <Link
          key={t.id}
          to={`/channels/${guildId}/${t.id}`}
          className="block rounded bg-bg-2 p-3 hover:bg-bg-hover"
        >
          <div className="flex items-center gap-2 font-medium text-text-0">
            <Icon
              name={t.type === ChannelType.PRIVATE_THREAD ? 'lock' : 'thread'}
              className="h-4 w-4 text-text-3"
            />
            <span className="truncate">{t.name}</span>
          </div>
          <div className="text-xs text-text-3">
            {t.threadMetadata?.messageCount ?? 0} messages · last active{' '}
            {new Date(t.threadMetadata?.lastActivityAt ?? Date.now()).toLocaleString()}
          </div>
        </Link>
      ))}
    </SidePanel>
  );
}
